import { Avatar, Input, Button } from 'antd';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { SendOutlined, CloseOutlined } from '@ant-design/icons';
import { useSearchChatMessages } from '@/hooks/chat/useSearchChatMessages';
import { ChatListItem } from '@/infrastructure/domain/chat';
import { useSendChatMessage } from '@/hooks/chat/useSendChatMessage';
import ChatMessageItem from './ChatMessageItem';

type Props = {
  chatItem: ChatListItem;
  isModalView?: boolean;
  showChatBox?: Dispatch<SetStateAction<boolean>>;
};

const ChatBox = ({ chatItem, isModalView, showChatBox }: Props) => {
  const { data: response } = useSearchChatMessages(chatItem.id);
  const sendChatMessage = useSendChatMessage(chatItem.id);
  const chatMessages = response?.data;

  const [message, setMessage] = useState<string>('');

  const chatRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSendChatMessage = async () => {
    if (!sendChatMessage) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to send chat message',
        text: 'Please try again later. If issue persist contact support.',
      });
      return;
    }

    try {
      await sendChatMessage?.mutateAsync({
        roomId: chatItem.id,
        payload: {
          senderId: chatItem.senderId,
          receiverId: chatItem.receiverId,
          message,
        },
      });

      setMessage('');
    } catch (err) {
      /** TODO: make sure to reflect errors in chat */
    }
  };

  return (
    <div
      className={`shadow-lg bg-gray-100 border overflow-hidden flex flex-col ${isModalView ? 'mr-5 w-[500px] h-[500px]' : 'w-full h-full max-h-screen flex-1'}`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="flex items-center gap-2">
          <Avatar src={chatItem?.profile} size={32} />
          <h2 className="font-semibold text-sm">{chatItem.name}</h2>
        </div>
        {showChatBox && (
          <Button
            icon={<CloseOutlined />}
            size="small"
            shape="circle"
            onClick={() => {
              showChatBox(false);
            }}
          />
        )}
      </div>

      <div ref={chatRef} className="flex-1 overflow-auto p-4 space-y-4">
        {chatMessages &&
          chatMessages.map(message => (
            <ChatMessageItem chatMessage={message} />
          ))}
        {!chatMessages?.length && (
          <p className="flex justify-center text-sm text-gray-500">
            Start a conversation
          </p>
        )}
      </div>

      <div className="p-3 border-t bg-white flex items-center gap-2">
        <Input
          value={message}
          className="flex-1"
          placeholder="Type a message"
          onChange={e => setMessage(e.target.value)}
        />
        <Button
          type="primary"
          shape="circle"
          icon={<SendOutlined />}
          onClick={handleSendChatMessage}
        />
      </div>
    </div>
  );
};

export default ChatBox;
